// api.js

import {
  fastApiAuthAxios,
  FAST_API_RATE_LIMIT_MESSAGE,
} from "./Fast_interceptor";

// True when a caught error (or an already-unwrapped response body) is a
// FastAPI rate-limit (429) response — IP-based or user-id based.
export const isFastApiRateLimitError = (err) =>
  err?.response?.status === 429 ||
  err?.status_code === 429 ||
  err?.response?.data?.error_code === "RATE_LIMIT_EXCEEDED" ||
  err?.error_code === "RATE_LIMIT_EXCEEDED" ||
  err?.rateLimited === true;

// Normalized result the wrappers return on a rate-limit hit. The user-facing
// toast is raised once, centrally, by the FastAPI response interceptor; callers
// only need to notice `rateLimited` and stop (no second toast).
const rateLimitedResult = () => ({
  success: false,
  rateLimited: true,
  error_code: "RATE_LIMIT_EXCEEDED",
  message: FAST_API_RATE_LIMIT_MESSAGE,
});

export const getReadableApiErrorMessage = (
  value,
  fallback = "Something went wrong"
) => {
  if (!value) return fallback;

  if (
    value?.error_code === "RATE_LIMIT_EXCEEDED" ||
    value?.rateLimited === true ||
    value?.status_code === 429
  ) {
    return FAST_API_RATE_LIMIT_MESSAGE;
  }

  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    const messages = value
      .map((item) => getReadableApiErrorMessage(item, ""))
      .filter(Boolean);
    return messages.length > 0 ? messages.join(", ") : fallback;
  }

  if (typeof value === "object") {
    return (
      value?.message ||
      value?.errorText ||
      value?.error_value?.errorText ||
      value?.error?.message ||
      value?.error?.errorText ||
      value?.detail?.message ||
      value?.detail ||
      fallback
    );
  }

  return String(value);
};




// Upload PDF to FastAPI
export const uploadPdf = async (file, collection_name = "RagCollection") => {
  const formData = new FormData();

  formData.append("file", file);
  formData.append("collection_name", collection_name);

  try {
    const res = await fastApiAuthAxios.post(
      "documents/uploadPdf",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return res.data;
  } catch (err) {
    console.error("Upload PDF failed:", err);

    if (isFastApiRateLimitError(err)) return rateLimitedResult();

    return (
      err.response?.data || {
        error: err.message,
      }
    );
  }
};


// Retry pending document in FastAPI
export const retryPendingDocument = async (documentId) => {
  try {
    const res = await fastApiAuthAxios.post(
      `documents/${documentId}/retry`
    );

    return res.data;
  } catch (err) {
    console.error("Error retrying pending document:", err);

    if (isFastApiRateLimitError(err)) return rateLimitedResult();

    return (
      err.response?.data || {
        error: err.message || "Retry request failed",
      }
    );
  }
};


// Retry edit-pending document in FastAPI
export const retryEditPendingDocument = async (documentId) => {
  try {
    const res = await fastApiAuthAxios.post(
      `documents/${documentId}/retry-pending`
    );
    return res.data;
  } catch (err) {
    console.error("Error retrying edit-pending document:", err);
    if (isFastApiRateLimitError(err)) return rateLimitedResult();
    return {
      error: getReadableApiErrorMessage(
        err?.response?.data?.error ||
          err?.response?.data?.error_value ||
          err?.response?.data ||
          err,
        "Retry request failed"
      ),
    };
  }
};

// Delete document in FastAPI
export const deleteDocument = async (documentId) => {
  try {
    const res = await fastApiAuthAxios.delete(`documents/${documentId}`);
    return res.data;
  } catch (err) {
    console.error("Error deleting document:", err);
    if (isFastApiRateLimitError(err)) return rateLimitedResult();
    return {
      error: getReadableApiErrorMessage(
        err?.response?.data?.error ||
          err?.response?.data?.error_value ||
          err?.response?.data ||
          err,
        "Delete request failed"
      ),
    };
  }
};

// Retry delete-pending document in FastAPI
export const retryDeleteDocument = async (documentId) => {
  try {
    const res = await fastApiAuthAxios.post(
      `documents/${documentId}/retry-delete`
    );
    return res.data;
  } catch (err) {
    console.error("Error retrying document delete:", err);
    if (isFastApiRateLimitError(err)) return rateLimitedResult();
    return {
      error: getReadableApiErrorMessage(
        err?.response?.data?.error ||
          err?.response?.data?.error_value ||
          err?.response?.data ||
          err,
        "Retry delete request failed"
      ),
    };
  }
};

// Restore a deleted document (re-syncs vectors into Qdrant) in FastAPI
export const restoreDocument = async (documentId) => {
  try {
    const res = await fastApiAuthAxios.post(`documents/${documentId}/restore`);
    return res.data;
  } catch (err) {
    console.error("Error restoring document:", err);
    if (isFastApiRateLimitError(err)) return rateLimitedResult();
    return {
      error: getReadableApiErrorMessage(
        err?.response?.data?.error ||
          err?.response?.data?.error_value ||
          err?.response?.data ||
          err,
        "Restore request failed"
      ),
    };
  }
};

// Permanently delete a document (irreversible) in FastAPI
export const permanentlyDeleteDocument = async (documentId) => {
  try {
    const res = await fastApiAuthAxios.delete(
      `documents/${documentId}/permanent`
    );
    return res.data;
  } catch (err) {
    console.error("Error permanently deleting document:", err);
    if (isFastApiRateLimitError(err)) return rateLimitedResult();
    return {
      error: getReadableApiErrorMessage(
        err?.response?.data?.error ||
          err?.response?.data?.error_value ||
          err?.response?.data ||
          err,
        "Permanent delete request failed"
      ),
    };
  }
};

// Bulk edit document chunks in FastAPI
export const bulkEditDocumentChunks = async (payload) => {
  try {
    const res = await fastApiAuthAxios.put(
      "documents/chunks/bulk-edit",
      payload
    );
    return res.data;
  } catch (err) {
    console.error("Error bulk editing document chunks:", err);
    if (isFastApiRateLimitError(err)) return rateLimitedResult();
    const data = err?.response?.data;
    return {
      ...(data && typeof data === "object" ? data : {}),
      error: getReadableApiErrorMessage(
        data?.error || data?.error_value || data || err,
        "Bulk edit request failed"
      ),
    };
  }
};



// Get client detection emails
export const getClientDetectionEmails = async (
  page = 1,
  pageSize = 10
) => {
  try {
    const res = await fastApiAuthAxios.get(
      "/email/getClientDetectionEmails",
      {
        params: {
          page,
          page_size: pageSize,
        },
      }
    );

    console.info("API Response:", res.data);

    return res.data;
  } catch (err) {
    console.error(
      "API Error:",
      err.response?.data || {
        error: err.message,
      }
    );

    if (isFastApiRateLimitError(err)) return rateLimitedResult();

    return (
      err.response?.data || {
        error: err.message,
      }
    );
  }
};

  // Toggle a feature's active status
// PATCH /features/{feature_id}/toggle
export const toggleFeature = async (featureId, isActive) => {
  try {
    const res = await fastApiAuthAxios.patch(
      `/features/${featureId}/toggle`,
      {
        is_active: isActive,
      }
    );

    console.info("API Response:", res.data);

    return res.data;
  } catch (err) {
    console.error(
      "API Error:",
      err.response?.data || {
        error: err.message,
      }
    );

    if (isFastApiRateLimitError(err)) return rateLimitedResult();

    return (
      err.response?.data || {
        error: err.message,
      }
    );
  }
};


// Fetch all features
// GET /features/getAllfeatures
export const getAllFeatures = async () => {
  try {
    const res = await fastApiAuthAxios.get("/features/getAllfeatures");

    console.info("API Response:", res.data);

    return res.data;
  } catch (err) {
    console.error(
      "API Error:",
      err.response?.data || {
        error: err.message,
      }
    );

    if (isFastApiRateLimitError(err)) return rateLimitedResult();

    return (
      err.response?.data || {
        error: err.message,
      }
    );
  }
};


// GET /features/getAllfeatures
export const checkSystemHealthCheck = async () => {
  try {
    const res = await fastApiAuthAxios.get("/health/ready");
    console.info("API Response:", res.data);
    return res.data;
  } catch (err) {
    console.error(
      "API Error:",
      err.response?.data || {
        error: err.message,
      }
    );

    if (isFastApiRateLimitError(err)) return rateLimitedResult();

    return (
      err.response?.data || {
        error: err.message,
      }
    );
  }
};



// GET /client/getClientDetailsByUserIdAndDatetime
export const getClientDetailsByUserIdAndDatetime = async (
  userId,
  createdAt,
  windowMinutes = 5
) => {
  try {
    const res = await fastApiAuthAxios.get(
      "/client/getClientDetailsByUserIdAndDatetime",
      {
        params: {
          user_id: userId,
          created_at: createdAt,
          window_minutes: windowMinutes,
        },
      }
    );

    console.info("API Response:", res.data);

    return res.data;
  } catch (err) {
    console.error(
      "API Error:",
      err.response?.data || {
        error: err.message,
      }
    );

    if (isFastApiRateLimitError(err)) return rateLimitedResult();

    return (
      err.response?.data || {
        error: err.message,
      }
    );
  }
};


// GET /client/getUserChatByDateRange
export const getUserChatByDateRange = async (
  userId,
  fromDate,
  toDate,
  page = 1,
  pageSize = 50
) => {
  try {
    const res = await fastApiAuthAxios.get(
      "/chat/getUserChatByDateRange",
      {
        params: {
          user_id: userId,
          from_date: fromDate,
          to_date: toDate,
          page: page,
          page_size: pageSize,
        },
      }
    );

    console.info("API Response:", res.data);

    return res.data;
  } catch (err) {
    console.error(
      "API Error:",
      err.response?.data || {
        error: err.message,
      }
    );

    if (isFastApiRateLimitError(err)) return rateLimitedResult();

    return (
      err.response?.data || {
        error: err.message,
      }
    );
  }
};